import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertTriangle, Clock, Lock, LifeBuoy, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { api, ApiError } from '@/lib/api-client';
/** Bandeaux d'état sous l'en-tête : abonnement de la ville, accès support GEOSIRA. */
export function StatusBanners() {
    const { user } = useAuth();
    const { currentTenant, role } = useTenant();
    if (!currentTenant || !user)
        return null;
    // Le super admin voit tous les signalements ; seul le support a besoin de l'accord de la ville
    const isSupport = !user.isSuperAdmin && user.platformRole === 'SUPPORT';
    const now = Date.now();
    const trialEnd = currentTenant.trialEndsAt ? new Date(currentTenant.trialEndsAt).getTime() : null;
    const trialDays = trialEnd ? Math.ceil((trialEnd - now) / 86_400_000) : null;
    const banners = [];
    if (currentTenant.status === 'SUSPENDED') {
        banners.push(_jsxs(Banner, { tone: "danger", icon: _jsx(Lock, { className: "size-4" }), children: ["L'acc\u00E8s de ", currentTenant.name, " \u00E0 CI-VILLE est suspendu", currentTenant.suspendedReason ? ` : ${currentTenant.suspendedReason}` : '', ". Contactez GEOSIRA."] }, "susp"));
    }
    else if (currentTenant.status === 'PAST_DUE') {
        banners.push(_jsxs(Banner, { tone: "warning", icon: _jsx(AlertTriangle, { className: "size-4" }), children: ["Abonnement en retard de paiement : la plateforme est en lecture seule.", ' ', role === 'ADMIN' && _jsx(Link, { to: "/ma-ville", className: "font-semibold underline", children: "Voir les factures" })] }, "due"));
    }
    else if (currentTenant.status === 'TRIAL' && trialDays != null) {
        banners.push(trialDays < 0 ? (_jsx(Banner, { tone: "warning", icon: _jsx(Clock, { className: "size-4" }), children: "La p\u00E9riode d'essai est termin\u00E9e : la plateforme est en lecture seule jusqu'\u00E0 la signature du contrat." }, "trial")) : trialDays <= 10 ? (_jsxs(Banner, { tone: "info", icon: _jsx(Clock, { className: "size-4" }), children: ["P\u00E9riode d'essai : ", trialDays, " jour", trialDays > 1 ? 's' : '', " restant", trialDays > 1 ? 's' : '', "."] }, "trial")) : null);
    }
    return (_jsxs(_Fragment, { children: [banners, isSupport && _jsx(SupportBanner, { tenantId: currentTenant.id, tenantName: currentTenant.name })] }));
}
function SupportBanner({ tenantId, tenantName }) {
    const qc = useQueryClient();
    const [asking, setAsking] = useState(false);
    const { data: grant } = useQuery({
        queryKey: ['support-active', tenantId],
        // Réponse vide quand aucun accès n'est ouvert : TanStack Query exige une valeur définie
        queryFn: async () => (await api.support.active(tenantId)) ?? null,
        refetchInterval: 60_000,
    });
    const ask = async (emergency) => {
        const reason = window.prompt(emergency
            ? `Accès d'urgence de 2 h à ${tenantName} (la ville en est informée). Motif détaillé :`
            : `Demande d'accès aux signalements de ${tenantName}. Motif :`);
        if (!reason?.trim())
            return;
        setAsking(true);
        try {
            if (emergency)
                await api.support.emergency(tenantId, reason.trim());
            else
                await api.support.request(tenantId, reason.trim(), 24);
            toast.success(emergency ? 'Accès d\'urgence ouvert' : 'Demande envoyée aux administrateurs de la ville');
            qc.invalidateQueries({ queryKey: ['support-active', tenantId] });
            qc.invalidateQueries({ queryKey: ['reports'] });
        }
        catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Demande impossible');
        }
        finally {
            setAsking(false);
        }
    };
    if (grant) {
        return (_jsxs(Banner, { tone: grant.status === 'EMERGENCY' ? 'danger' : 'info', icon: _jsx(LifeBuoy, { className: "size-4" }), children: ["Acc\u00E8s support ", grant.status === 'EMERGENCY' ? 'd\'urgence ' : '', "actif \u00E0 ", tenantName, " jusqu'au", ' ', format(new Date(grant.expiresAt), 'd MMM à HH:mm', { locale: fr }), ". Vos consultations sont journalis\u00E9es."] }));
    }
    return (_jsxs(Banner, { tone: "neutral", icon: _jsx(ShieldAlert, { className: "size-4" }), children: ["Vue GEOSIRA : statistiques seulement. Les signalements de ", tenantName, " contiennent des renseignements personnels.", ' ', _jsx("button", { className: "font-semibold underline", disabled: asking, onClick: () => ask(false), children: "Demander l'acc\u00E8s" }), ' · ', _jsx("button", { className: "underline opacity-80", disabled: asking, onClick: () => ask(true), children: "Acc\u00E8s d'urgence" })] }));
}
// Bandeaux neutres : la tonalité ne colore que l'icône (voir .claude/skills/interface-sobre)
const TONES = {
    info: 'text-primary',
    warning: 'text-warning',
    danger: 'text-destructive',
    neutral: 'text-muted-foreground',
};
function Banner({ tone, icon, children }) {
    return (_jsxs("div", { className: "flex items-center gap-2 border-b border-border bg-muted px-4 py-1.5 text-xs text-foreground md:px-6", role: "status", children: [_jsx("span", { className: `shrink-0 ${TONES[tone]}`, children: icon }), _jsx("span", { children: children })] }));
}
//# sourceMappingURL=StatusBanners.js.map